import React from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../Context/AuthContext'

// Derive a human-readable display name from whatever is available in the user object
const getDisplayName = (user) => {
  if (!user) return 'User'
  const firstName = user.FirstName?.trim()
  const lastName = user.LastName?.trim()
  if (firstName && lastName) return `${firstName} ${lastName}`
  if (firstName) return firstName
  if (lastName) return lastName
  // UserName might be a full name (not an email)
  if (user.UserName && !user.UserName.includes('@')) return user.UserName
  // Fall back to the part before @ in email
  if (user.Email) return user.Email.split('@')[0]
  return 'User'
}

const getInitials = (user) => {
  if (!user) return '?'
  const firstName = user.FirstName?.trim()
  const lastName = user.LastName?.trim()
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()
  if (firstName) return firstName[0].toUpperCase()
  if (user.Email) {
    const [local] = user.Email.split('@')
    // Use first two chars of the local part as initials
    return local.slice(0, 2).toUpperCase()
  }
  return '?'
}

const Header = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const menuOpen = Boolean(anchorEl)

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget)
  const handleCloseMenu = () => setAnchorEl(null)

  const handleLogout = () => {
    handleCloseMenu()
    logout()
    navigate('/')
  }

  const displayName = getDisplayName(user)
  const email = user?.Email || ''
  const initials = getInitials(user)

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{ bgcolor: '#111', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: 60, px: { xs: 2, sm: 3 } }}>

        {/* Left — logo + brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <img src="/logo-light.png" alt="Vizybility" height={38} />
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', lineHeight: 1 }}>
            <Typography
              variant="caption"
              fontWeight={500}
              letterSpacing={1.5}
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', lineHeight: 1.2, userSelect: 'none' }}
            >
              Vizybility
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              letterSpacing={0.3}
              sx={{ color: '#fff', lineHeight: 1.2, userSelect: 'none' }}
            >
              Rental Board
            </Typography>
          </Box>
        </Box>

        {/* Right — clickable profile chip */}
        <Tooltip title="Account" placement="bottom-end">
          <Box
            onClick={handleOpenMenu}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: menuOpen ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.07)',
              borderRadius: 2,
              px: 1.4,
              py: 0.6,
              cursor: 'pointer',
              transition: 'background 0.2s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2', fontSize: 13, fontWeight: 700 }}>
              {initials}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#fff', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                {displayName}
              </Typography>
              {email && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                  {email}
                </Typography>
              )}
            </Box>
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 18,
                color: 'rgba(255,255,255,0.5)',
                transition: 'transform 0.2s',
                transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </Box>
        </Tooltip>

        {/* Dropdown menu */}
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleCloseMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              elevation: 4,
              sx: {
                mt: 0.8,
                minWidth: 220,
                borderRadius: 2,
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 18,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            },
          }}
        >
          {/* Profile info header inside menu */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: '#1976d2', fontSize: 15, fontWeight: 700 }}>
                {initials}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                  {displayName}
                </Typography>
                {email && (
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                    {email}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          <MenuItem
            onClick={handleLogout}
            sx={{
              gap: 1.5,
              py: 1.2,
              color: '#d32f2f',
              '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' },
            }}
          >
            <LogoutIcon fontSize="small" />
            <Typography variant="body2" fontWeight={600}>Logout</Typography>
          </MenuItem>
        </Menu>

      </Toolbar>
    </AppBar>
  )
}

export default Header
